import React from 'react';
import { Text, View, StyleSheet, Svg, Path, Font } from '@react-pdf/renderer';

Font.register({
  family: "Open Sans",
  fonts: [
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf", fontWeight: "normal" },
    { src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf", fontWeight: "bold" }
  ]
});

// Styles

const styles = StyleSheet.create({
  page: { flexDirection: 'row', justifyContent: 'center', padding: 30 },
  chartContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: "space-between", paddingHorizontal: 50 },
  svg: { width: 200, height: 200 },
  legend: { display: 'flex', flexDirection: 'column', gap: 6 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  colorBox: { width: 10, height: 10, borderRadius: 3 },
  legendText: {
    fontSize: 8,
    fontFamily: "Open Sans", // Must match registered name
    fontWeight: "bold", // Ensures the bold variant is used
  },
  sliceText: { // Added style for slice text
    fontSize: 8,
    fontWeight: "bold",
    fill: "black",
    textAnchor: "middle",
  }
});


const PieChart = ({ data, colors }) => {
  const transformData = () => {
    return data.slice(1).reduce((acc, [label, value]) => {
      const existing = acc.find((item) => item.label === label);
      if (existing) {
        existing.value += value;
      } else {
        acc.push({ label: label, value: value, color: colors[label] || "#ccc" });
      }
      return acc;
    }, []);
  };

  const transformedData = transformData();
  const total = transformedData.reduce((sum, item) => sum + item.value, 0);
  let cumulativeAngle = 0;

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const getPieSlicePath = (centerX, centerY, radius, startAngle, endAngle) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    const d = [
      "M",
      centerX,
      centerY,
      "L",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      "Z",
    ].join(" ");

    return d;
  };

  return (
    <View>
      <View style={styles.chartContainer}>
        <Svg style={styles.svg} viewBox="0 0 200 200">
          {transformedData.map((item, index) => {
            const sliceAngle = (item.value / total) * 360;
            const startAngle = cumulativeAngle;
            const endAngle = cumulativeAngle + sliceAngle;
            cumulativeAngle += sliceAngle;

            const midAngle = (startAngle + endAngle) / 2;
            let textRadius = 80; // Default radius for text

            const textPosition = polarToCartesian(100, 100, textRadius, midAngle);
            
            const percentage = Math.floor((item.value / total) * 100);
            const percentageWithSpace = percentage > 9 ? percentage.toString().split('').join('') + '%' : percentage + '%';

            return (
              <React.Fragment key={index}>
                <Path
                  d={getPieSlicePath(100, 100, 100, startAngle, endAngle)}
                  fill={colors[index % colors.length]}
                  stroke="#fff"
                  strokeWidth="1"
                />
                <Text
                  x={textPosition.x}
                  y={textPosition.y}
                  style={styles.sliceText}
                >
                  {percentageWithSpace}
                </Text>
              </React.Fragment>
            );
          })}
        </Svg>

        {/* Side Legends */}
        <View style={styles.legend}>
          {transformedData.map((item, index) => (
            <View key={index} style={styles.legendItem}>
              <View style={[styles.colorBox, { backgroundColor: colors[index % colors.length] }]} />
              <Text style={styles.legendText}>
                {item.label} ({Math.floor((item.value / total) * 100)}%)
              </Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

export default PieChart;

